import { z } from 'zod';

import { dateTimeSchema, localDateTimeSchema } from '@apple/utils/globalization/models';

import { type InventoryStatus, inventoryStatusSchema } from '@apple/features/product/models/shared';

import { type ProductKey, productKeySchema } from './product';

/**
 * @see AppleBrandedPrograms.Web.Features.Catalog.ProductByWarehouse
 * @see AppleRetail.Web.Features.Catalog.ProductByWarehouse
 */
export interface BaseProductListing {
	isEnabledInErp: boolean;
	isEndOfLifeComplete: boolean;
	isActive: boolean;
	itemId: ProductKey;
	sku: string;
	unitOfMeasure: string;
	requiresApproval: boolean;
	isBackorderable: boolean;
	warehouseCode: string;
	currencyCode: string;
	productName: string;
	availableQuantity: number;
	customerCount: number;
	commercialPrice: number;
	isNewProductIntroductionComplete: boolean;
	hasImages: boolean;
	priceOverride?: number | null;
	activeDate?: Date | string | null;
	inactiveDate?: Date | string | null;
	commercialPriceUSD?: number | null;
}

/** AppleBrandedPrograms.Web.Features.Catalog.ProductByWarehouse */
/** AppleRetail.Web.Features.Catalog.ProductByWarehouse */
export const baseProductListingSchema = z.object({
	itemId: productKeySchema,
	sku: z.string(),
	productName: z.string(),
	warehouseCode: z.string(),
	availableQuantity: z.number(),
	customerCount: z.number(),
	unitOfMeasure: z.string(),
	commercialPrice: z.number(),
	currencyCode: z.string(),
	commercialPriceUSD: z.number().optional().nullable(),
	priceOverride: z.number().optional().nullable(),
	isEnabledInErp: z.boolean(),
	requiresApproval: z.boolean(),
	isBackorderable: z.boolean(),
	isNewProductIntroductionComplete: z.boolean(),
	isEndOfLifeComplete: z.boolean(),
	isActive: z.boolean(),
	activeDate: dateTimeSchema.nullable(),
	inactiveDate: dateTimeSchema.nullable(),
	hasImages: z.boolean(),
}) satisfies z.ZodType<BaseProductListing>;

export interface ProductAuditListing {
	dateTime: Date;
	username: string;
	event: string;
	itemId: ProductKey | null;
	userComments: string | null;
}

export interface ProductAuditFilters extends Partial<ProductAuditListing> {}

/** Manzanita.Web.Models.ShopProductInventoryModel */
export interface ShopProductInventory {
	sku: string;
	availableToPurchase?: number | null;
	itemState: InventoryStatus;
	warehouseCode: string;
	warehouseCountryCode: string;
	estimatedInStockDate: Date | string | null;
	commercialPrice: string;
}

export const productAuditListingSchema = z.object({
	dateTime: z.date(),
	username: z.string(),
	event: z.string(),
	itemId: productKeySchema,
	userComments: z.string().nullable(),
}) satisfies z.ZodType<ProductAuditListing>;

export const productAuditFiltersSchema =
	productAuditListingSchema.partial() satisfies z.ZodType<ProductAuditFilters>;

/** Manzanita.Web.Models.ShopProductInventoryModel */
export const shopProductInventorySchema = z.object({
	sku: z.string(),
	availableToPurchase: z.number().nullable(),
	itemState: inventoryStatusSchema,
	warehouseCode: z.string(),
	warehouseCountryCode: z.string(),
	estimatedInStockDate: localDateTimeSchema.nullable().or(z.string().nullable()),
	commercialPrice: z.string(),
}) satisfies z.ZodType<ShopProductInventory>;
